import { useCallback, useMemo } from 'react';

import {
  Offer,
  SavedCard,
  OrderResponse,
  PriceBreakdown,
  GetBootResponse,
  EPaymentProvider,
  SubscriptionsSummary,
  GetSubscriptionResponse,
  FinalizeSubscriptionResponse,
  CreatePaymentSessionResponse,
  CalculateEstimatedTaxResponse,
  GetAddressSuggestionsResponse,
} from '../contracts';
import { get, post } from '../apiUtil';
import { useForter } from '../hooks/use-forter';
import { APIRoutes, APIRoutes_V1 } from '../constants/enums';

function getAPIRoute(route: APIRoutes) {
  const baseURL = APIRoutes.BASE_URL_V3;
  const fullRoute = `${baseURL}${route ? '/' : ''}${route}`;
  return fullRoute;
}

export function useAPI() {
  const { forterTokenCookie } = useForter();

  const getCheckoutSummary = useCallback(() => {
    return get<Offer>(getAPIRoute(APIRoutes.GET_CHECKOUT_SUMMARY));
  }, []);

  const getSubscriptionsCheckoutSummary = useCallback(() => {
    return get<SubscriptionsSummary>(`${APIRoutes.GET_SUBSCRIPTIONS_CHECKOUT_SUMMARY}`);
  }, []);

  async function getOrder(orderId: string) {
    return get<OrderResponse>(`${getAPIRoute(APIRoutes.ORDERS)}/${orderId}`);
  }

  async function getSubscription(subscriptionId: string) {
    return get<GetSubscriptionResponse>(`${APIRoutes.GET_SUBSCRIPTION}/${subscriptionId}`);
  }

  async function getEstimatedTaxforZipCode(zipCode: string) {
    return get<CalculateEstimatedTaxResponse>(
      `${getAPIRoute(APIRoutes.CALCULATE_ESTIMATED_TAX)}?zipCode=${zipCode}`,
    );
  }

  async function getEstimatedTaxforAddress(address: string, zipCode: string) {
    return get<CalculateEstimatedTaxResponse>(
      `${getAPIRoute(APIRoutes.CALCULATE_ESTIMATED_TAX)}?address=${address}&zipCode=${zipCode}`,
    );
  }

  async function getSubscriptionEstimatedTaxforZipCode(zipCode: string) {
    return get<CalculateEstimatedTaxResponse>(
      `${APIRoutes.CALCULATE_SUBSCRIPTION_ESTIMATED_TAX}?zipCode=${zipCode}`,
    );
  }

  async function getBoot(domain: string, checkoutToken: string) {
    if (checkoutToken) {
      const headers = {
        'x-checkout-token': checkoutToken,
      };
      return get<GetBootResponse>(`${getAPIRoute(APIRoutes.BOOT)}`, { headers });
    }
    return get<GetBootResponse>(`${APIRoutes.BASE_URL}/${domain}/${APIRoutes.BOOT}`);
  }

  async function log(body: any) {
    return post(getAPIRoute(APIRoutes.LOG), body);
  }

  async function cancelOrder() {
    return post(getAPIRoute(APIRoutes.CANCEL));
  }

  async function closeSubscription() {
    return post(APIRoutes.CLOSE_SUBSCRIPTION);
  }

  async function createNuveiSession(paymentMethodId: string, email: string) {
    return post<CreatePaymentSessionResponse>(getAPIRoute(APIRoutes.NUVEI_SESSION), {
      paymentMethodId,
      receipt_email: email,
    });
  }

  async function finalizePayment<T>(body: any) {
    const bodyWithForterToken = {
      ...body,
      params: { ...body.params, fraudDetection: { forterTokenCookie } },
    };
    return post<T>(getAPIRoute(APIRoutes.FINALIZE_PAYMENT), bodyWithForterToken);
  }

  async function finalizeSubscriptionPayment(body: any) {
    return post<FinalizeSubscriptionResponse>(`${APIRoutes.FINALIZE_SUBSCRIPTION_PAYMENT}`, body);
  }

  async function getPaymentStatus<T>(sessionToken: string, paymentMethodId: string) {
    return post<T>(`${getAPIRoute(APIRoutes.NUVEI_PAYMENT_STATUS)}`, {
      sessionToken,
      paymentMethodId,
    });
  }

  async function getAdyenPaymentStatus<T>(redirectResult: string, paymentMethodId: string) {
    return post<T>(`${getAPIRoute(APIRoutes.NUVEI_PAYMENT_STATUS)}`, {
      redirectResult,
      paymentMethodId,
    });
  }

  async function getAddressSuggestions(address: string, signal?: AbortSignal) {
    const body = { fullAddress: address };
    return post<GetAddressSuggestionsResponse>(
      getAPIRoute(APIRoutes.GET_ADDRESS_SUGGESTIONS),
      body,
      {
        signal,
      },
    );
  }

  async function applyPromoCode(promoCodeName: string) {
    return get<PriceBreakdown>(
      `${getAPIRoute(APIRoutes.APPLY_PROMO_CODE)}?promoCodeName=${promoCodeName}`,
    );
  }

  async function cancelPromoCode() {
    return get<PriceBreakdown>(getAPIRoute(APIRoutes.CANCEL_PROMO_CODE));
  }

  async function removeSavedCard(
    pspCardIdentifier: SavedCard['pspCardIdentifier'],
    paymentProvider: EPaymentProvider,
  ) {
    return post<{}>(getAPIRoute(APIRoutes.REMOVE_SAVED_CARD), {
      pspCardIdentifier,
      paymentProvider,
    });
  }

  const API = useMemo(
    () => ({
      log,
      getBoot,
      getOrder,
      cancelOrder,
      applyPromoCode,
      getSubscription,
      cancelPromoCode,
      removeSavedCard,
      finalizePayment,
      closeSubscription,
      createNuveiSession,
      getCheckoutSummary,
      getPaymentStatus,
      getAddressSuggestions,
      getAdyenPaymentStatus,
      getEstimatedTaxforZipCode,
      getEstimatedTaxforAddress,
      finalizeSubscriptionPayment,
      getSubscriptionsCheckoutSummary,
      getSubscriptionEstimatedTaxforZipCode,
    }),
    [forterTokenCookie],
  );

  return { API };
}

async function getOrder(orderId: string) {
  return get<OrderResponse>(`${APIRoutes_V1.ORDERS}/${orderId}`);
}

async function getBoot(domain: string, checkoutToken: string) {
  if (checkoutToken) {
    const headers = {
      'x-checkout-token': checkoutToken,
    };
    return get<GetBootResponse>(`${APIRoutes_V1.BASE_API}/${APIRoutes_V1.BOOT}`, { headers });
  }
  return get<GetBootResponse>(`${APIRoutes_V1.BASE_API}/${domain}/${APIRoutes_V1.BOOT}`);
}

export async function getAdyenPaymentStatus<T>(redirectResult: string, paymentMethodId: string) {
  return post<T>(`${getAPIRoute(APIRoutes.NUVEI_PAYMENT_STATUS)}`, {
    redirectResult,
    paymentMethodId,
  });
}
export const API_V1 = {
  getBoot,
  getOrder,
};
